import View               from '@widesk-ui/components/View';
import currentEditorStore from '@/TokamapEditor/stores/currentEditorStore';
import Segmented           from '@widesk-ui/Segmented';
import HelpTip             from '@/TokamapEditor/ux/AreaSidebar/HelpTip';
import Field               from '@/TokamapEditor/ux/AreaSidebar/Field';
import InputOk             from '@/TokamapEditor/ux/AreaSidebar/InputOk';
import SwitchField         from '@/TokamapEditor/ux/SwitchField';
import { observer }        from 'mobx-react-lite';
import linkUrlToShape      from '@/TokamapEditor/helpers/linkUrlToShape';
import { commonValue }     from '@/TokamapEditor/ux/AreaSidebar/multiSelectHelpers';
import { ShapeUrlAction }  from '@tokamap/core';

const OPEN_MODES: { value: NonNullable<ShapeUrlAction['openMode']>; label: string; }[] = [
	{ value: 'new_tab', label: `Nouvel onglet` },
	{ value: 'same_tab', label: `Même onglet` },
	{ value: 'overlay', label: `Sur la carte` },
];

/** Vide : pas de redirection configurée, valeur acceptée (voir `ShapeUrlAction.url`). Sinon, une
 * adresse absolue http(s) — un schéma comme `javascript:` serait exécuté par `window.open`/l'iframe
 * de la superposition (voir `ui/UrlOverlay`), donc explicitement rejeté ici, pas seulement les
 * adresses mal formées. */
function validateUrl(value: string): void {
	if (!value) return;

	let parsed: URL;

	try {
		parsed = new URL(value);
	} catch {
		throw new Error(`Adresse invalide — utilisez un format complet, ex. https://exemple.com`);
	}

	if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
		throw new Error(`L'adresse doit commencer par http:// ou https://`);
	}
}

const AreaUrlSection = () => <AreaUrlSectionContent />;

const AreaUrlSectionContent = observer(() => {
	const shapes = currentEditorStore.sidebarShapes;

	if (!shapes.length) return null;

	const url = commonValue(shapes, s => s.urlAction?.url);
	const openMode = commonValue(shapes, s => s.urlAction?.openMode) ?? 'new_tab';
	const confirmBeforeOpen = commonValue(shapes, s => s.urlAction?.confirmBeforeOpen) ?? false;

	const apply = (value: Partial<ShapeUrlAction>) => linkUrlToShape(shapes.map(s => s.id), {
		url: value.url ?? url ?? '',
		openMode: value.openMode ?? openMode,
		confirmBeforeOpen: value.confirmBeforeOpen ?? confirmBeforeOpen,
	});

	return (
		<View gap="md">
			<Field label="Adresse de la page">
				<InputOk
					placeholder="https://exemple.com"
					value={url}
					debounce={600}
					onSubmit={value => {
						const trimmed = (value || '').trim();

						validateUrl(trimmed);

						return apply({ url: trimmed });
					}}
				/>
			</Field>

			<Field
				label={(
					<>
						Ouverture
						<HelpTip>
							« Sur la carte » affiche la page par-dessus la carte, sans la quitter. Certains sites
							refusent d'être affichés ainsi : la fenêtre reste alors vide, sans qu'on puisse le
							détecter à l'avance. Préférez « Nouvel onglet » si le site n'est pas le vôtre.
						</HelpTip>
					</>
				)}
			>
				<Segmented
					block
					value={openMode}
					onChange={value => apply({ openMode: value as NonNullable<ShapeUrlAction['openMode']> })}
					options={OPEN_MODES}
				/>
			</Field>

			{/* Une superposition ne fait jamais quitter la carte (voir `ShapeUrlAction.confirmBeforeOpen`) — rien à confirmer. */}
			{openMode !== 'overlay' && (
				<SwitchField
					label={<>Demander confirmation<HelpTip>Évite qu'un clic accidentel ne fasse quitter la carte.</HelpTip></>}
					value={confirmBeforeOpen}
					onChange={checked => apply({ confirmBeforeOpen: checked })}
				/>
			)}
		</View>
	);
});

export default AreaUrlSection;
